import * as z from 'zod';

import type {BrandTagFragment} from '@/modules/commerce/brand/query';
import type {ProductColorFragment} from '@/modules/commerce/product-color/query';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';
import {assertUnreachable} from '@/shared/assertUnreachable';

import type {ProductFilters} from '../../filters/query';
import type {ItemForPriceFragment} from '../../item/query';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription, ProductDescriptionProperty} from '../description';
import {type ProductImageSide} from '../ProductImage';
import type {Overlay} from '../ProductImageStack';
import {getOverlayPlacement, type ProductImageStackDescription} from '../ProductImageStack';
import type {UnionProductCoreFragment} from '../query';

import {
	type ClothingPrintLocation,
	clothingPrintLocationConflicts,
} from './schema/clothingPrintLocation';
import {
	DOCUMENT_TYPE_CLOTHING_PRODUCT,
	DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT,
} from './schema/names';
import type {
	ClothingPrintOptionGroupCoreFragment,
	ClothingProductCoreFragment,
	ClothingProductFragment,
	CustomClothingProductCoreFragment,
	CustomClothingProductFragment,
} from './query';
import {findVariantForSelectedProperties} from './util';

export const clothingPrintOptionConfigurationSchema = z.object({
	print: z
		.object({
			collectionId: z.string(),
			printKey: z.string(),
		})
		.nullable(),
	printColorId: z.string().nullable(),
});

export type ClothingPrintOptionConfiguration = z.infer<
	typeof clothingPrintOptionConfigurationSchema
>;

export const clothingOptions = z.record(z.string(), clothingPrintOptionConfigurationSchema);

export type ClothingOptions = z.infer<typeof clothingOptions>;

export const clothingConfigurationSchema = z.object({
	variantId: z.string().nullable(),
	options: clothingOptions.nullable(),
});

export type ClothingConfiguration = z.infer<typeof clothingConfigurationSchema>;

export function parseClothingConfiguration(value: unknown): ClothingConfiguration | null {
	const result = clothingConfigurationSchema.safeParse(value);

	if (!result.success) {
		console.warn('Failed to parse clothing product configuration', result.error);
		return null;
	}

	return result.data;
}

export function isClothingProduct<P extends UnionProductCoreFragment>(
	product: P,
): product is Extract<
	P,
	{_type: typeof DOCUMENT_TYPE_CLOTHING_PRODUCT | typeof DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT}
> {
	return (
		product._type === DOCUMENT_TYPE_CLOTHING_PRODUCT ||
		product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT
	);
}

/**
 * Returns the simplest (but still valid) configuration of the product.
 */
export function getDefaultClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
): ClothingConfiguration {
	let variantId = null;
	if (product.variants && product.variants.length > 0) {
		variantId = product.variants[0]._key;
	}

	let options: ClothingOptions | null = null;

	if (product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT) {
		for (const optionGroup of product.optionGroups ?? []) {
			// For optionGroups that are required, we always want to provide a configured print.
			// This is important so the returned config is valid.
			if (optionGroup.required) {
				const printConfig = getInitialOptionGroupConfiguration(optionGroup);
				if (!printConfig) {
					// If there is not a valid print config for this option group, we can't provide
					// a valid configuration for the product either.
					continue;
				}

				if (!options) {
					options = {};
				}

				const optionGroupId = optionGroup._key;
				options[optionGroupId] = printConfig;
			}
		}
	}

	return {
		variantId: variantId,
		options: options,
	};
}

/**
 * Returns a initial configuration for the given option group. The function will try to find a
 * configuration combining a print with the most appropriate print color (if the selected print
 * is a single color print).
 */
const getInitialOptionGroupConfiguration = (
	optionGroup: ClothingPrintOptionGroupCoreFragment,
): ClothingPrintOptionConfiguration | null => {
	if (!optionGroup.printCollections || optionGroup.printCollections.length === 0) {
		return null;
	}

	const collection = optionGroup.printCollections.at(0);
	if (!collection) {
		return null;
	}

	switch (collection?._type) {
		case 'commerce.singleColorPrintCollection': {
			const print = collection.prints?.at(0);
			if (!print) {
				return null;
			}

			const color =
				print.preferredPrintColor ?? collection.availablePrintColors?.at(0) ?? null;

			if (!color) {
				return null;
			}

			return {
				print: {
					collectionId: collection._id,
					printKey: print._key,
				},
				printColorId: color._id,
			};
		}
		case 'commerce.multiColorPrintCollection': {
			const print = collection.prints?.at(0);
			if (!print) {
				return null;
			}

			return {
				print: {
					collectionId: collection._id,
					printKey: print._key,
				},
				printColorId: null,
			};
			break;
		}
		default:
			assertUnreachable(collection);
	}
};

export function clothingConfigurationFromFormData(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	formData: FormData,
): ClothingConfiguration | null {
	// Find the selected variant based the config in the FormData:
	const sizeId = formData.get('variantSizeId')?.toString();
	const baseColorId = formData.get('variantBaseColorId')?.toString();
	const printColorId = formData.get('variantPrintColorId')?.toString();
	const contrastColorId = formData.get('variantContrastColorId')?.toString();

	const variant = findVariantForSelectedProperties(
		product.variants ?? [],
		sizeId ?? null,
		baseColorId ?? null,
		printColorId ?? null,
		contrastColorId ?? null,
	);

	const variantId = variant?._key ?? null;

	// Find any options configured in the FormData:
	const options: ClothingOptions = {};

	if (product._type === 'commerce.customClothingProduct') {
		for (const optionGroup of product.optionGroups ?? []) {
			const enabled = formData.get(`options[${optionGroup._key}]`) === 'enabled';
			if (!enabled) {
				continue;
			}
			const optionGroupId = optionGroup._key;

			// If the option group is enabled we always want to provide a config. It doesn't have
			// to be a _valid_ config though, so fall back to one with no print configured. This
			// will force the user to make a selection.
			const printConfig = getInitialOptionGroupConfiguration(optionGroup) ?? {
				print: null,
				printColorId: null,
			};

			options[optionGroupId] = printConfig;

			// Now try to find an actual print config in the FormData and use that if it's valid:
			const printId = formData.get(`options[${optionGroupId}][printId]`)?.toString();
			const printColorId =
				formData.get(`options[${optionGroupId}][printColorId]`)?.toString() ?? null;

			if (!printId || printId === 'none') {
				continue;
			}

			const [collectionId, printKey] = printId.split(':');
			if (!collectionId || !printKey) {
				continue;
			}

			for (const printCollection of optionGroup.printCollections ?? []) {
				if (printCollection._id === collectionId) {
					for (const print of printCollection.prints ?? []) {
						if (print._key === printKey) {
							options[optionGroupId] = {
								print: {
									collectionId: collectionId,
									printKey: printKey,
								},
								printColorId: printColorId ?? null,
							};
							break;
						}
					}
				}
			}
		}
	}

	return {
		variantId,
		options,
	};
}

export type ClothingPrintOptionGroupValidationError = {
	print: string | null;
	printColor: string | null;
};

export type ClothingConfigurationValidationResult = {
	isValid: boolean;
	errors: {
		// Error describing issues with the variant selection:
		variant: string | null;

		// Error message describign issues with the combination of the selected options:
		options: Record<string, ClothingPrintOptionGroupValidationError> | null;
	};
};

export function validateClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	configuration: ClothingConfiguration,
) {
	const result: ClothingConfigurationValidationResult = {
		isValid: true,
		errors: {
			variant: null,
			options: null,
		},
	};

	// Check if the variantId is valid:
	const selectedVariant = product.variants?.find(
		(variant) => variant._key === configuration.variantId,
	);
	if (!selectedVariant) {
		result.isValid = false;
		result.errors.variant =
			'Denne kombinasjonen av egenskaper er dessverre ikke tilgjengelig. Forsøk å velge en annen kombinasjon.';
	}

	if (product._type === 'commerce.customClothingProduct' && configuration.options) {
		// Ensure all required option groups have a print selected:
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options[optionGroup._key];

			if (optionGroup.required && (!configuredOption || !configuredOption.print)) {
				result.isValid = false;

				if (!result.errors.options) {
					result.errors.options = {};
				}

				if (!Object.hasOwn(result.errors.options, optionGroup._key)) {
					result.errors.options[optionGroup._key] = {print: null, printColor: null};
				}

				result.errors.options[optionGroup._key].print = 'Du må velge et trykk.';
				continue;
			}
		}

		// Ensure there aren't conflicts between the selected prints:
		const selectedPrintLocationsByOptionGroupKey = new Map<
			string,
			{name: string; printLocation: ClothingPrintLocation}
		>();

		// First enumerate all selected print locations:
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options[optionGroup._key];
			if (!configuredOption || !configuredOption.print || !optionGroup.location) {
				continue;
			}

			selectedPrintLocationsByOptionGroupKey.set(optionGroup._key, {
				name: optionGroup.name ?? '',
				printLocation: optionGroup.location,
			});
		}

		// Loop through again and check for conflicts with each other configured option:
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options[optionGroup._key];
			if (!configuredOption || !configuredOption.print || !optionGroup.location) {
				continue;
			}

			const potentiallyConflictingLocations =
				clothingPrintLocationConflicts[optionGroup.location] || [];

			const conflicts = Array.from(selectedPrintLocationsByOptionGroupKey.values()).reduce(
				(acc, otherConfiguredOption) => {
					if (
						otherConfiguredOption.printLocation !== optionGroup.location &&
						potentiallyConflictingLocations.includes(
							otherConfiguredOption.printLocation,
						)
					) {
						acc.push(otherConfiguredOption.name);
					}

					return acc;
				},
				new Array<string>(),
			);

			if (conflicts.length > 0) {
				result.isValid = false;

				if (!result.errors.options) {
					result.errors.options = {};
				}

				if (!Object.hasOwn(result.errors.options, optionGroup._key)) {
					result.errors.options[optionGroup._key] = {print: null, printColor: null};
				}

				result.errors.options[optionGroup._key].print =
					`"${optionGroup.name}" kan ikke velges samtidig med "${conflicts.join('", "')}". Du må fjerne ett av valgene.`;
			}
		}

		// Make sure the user has selected a print color for each single color print:
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options[optionGroup._key];
			if (!configuredOption || !optionGroup.location) {
				continue;
			}

			if (!configuredOption.print) {
				// The option is configured, but no print has been selected
				result.isValid = false;

				if (!result.errors.options) {
					result.errors.options = {};
				}

				if (!Object.hasOwn(result.errors.options, optionGroup._key)) {
					result.errors.options[optionGroup._key] = {
						print: null,
						printColor: null,
					};
				}

				result.errors.options[optionGroup._key].print =
					`Du må velge et trykk i denne plasseringen.`;
			} else {
				for (const printCollection of optionGroup.printCollections ?? []) {
					if (printCollection._id === configuredOption.print.collectionId) {
						if (printCollection._type === 'commerce.singleColorPrintCollection') {
							if (!configuredOption.printColorId) {
								// The option is configured with a print, but no print color has been selected
								result.isValid = false;

								if (!result.errors.options) {
									result.errors.options = {};
								}

								if (!Object.hasOwn(result.errors.options, optionGroup._key)) {
									result.errors.options[optionGroup._key] = {
										print: null,
										printColor: null,
									};
								}

								result.errors.options[optionGroup._key].printColor =
									`Du må velge en trykkfarge for dette enfargetrykket.`;
								break;
							}
						}
					}
				}
			}
		}
	}

	return result;
}

export function isValidClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	configuration: ClothingConfiguration,
) {
	const result = validateClothingConfiguration(product, configuration);
	return result.isValid;
}

export function getItemsForClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	configuration: ClothingConfiguration,
) {
	const selectedVariant = product.variants?.find((v) => v._key === configuration.variantId);
	const selectedItems: ItemForPriceFragment[] = [];

	if (selectedVariant?.items?.length) {
		selectedItems.push(...selectedVariant.items);
	}

	if (product._type === 'commerce.customClothingProduct' && configuration.options) {
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options[optionGroup._key];
			if (!configuredOption || !configuredOption.print) {
				continue;
			}

			for (const printCollection of optionGroup.printCollections ?? []) {
				if (printCollection._id === configuredOption.print.collectionId) {
					for (const print of printCollection.prints ?? []) {
						if (print._key === configuredOption.print.printKey) {
							// Add correct item from the option group depending on the type of
							// print collection the print is from:
							if (
								printCollection._type === 'commerce.singleColorPrintCollection' &&
								optionGroup.singleColorPrintItem
							) {
								selectedItems.push(optionGroup.singleColorPrintItem);
							} else if (
								printCollection._type === 'commerce.multiColorPrintCollection' &&
								optionGroup.multiColorPrintItem
							) {
								selectedItems.push(optionGroup.multiColorPrintItem);
							}
						}
					}
				}
			}
		}
	}

	return selectedItems;
}

export function getProductNameForClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	_configuration: ClothingConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForClothingConfiguration(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	configuration: ClothingConfiguration,
) {
	const desc: ProductDescription = [];

	const variant = product.variants?.find((variant) => variant._key == configuration.variantId);

	if (variant && variant.properties && variant.properties.length > 0) {
		const variantDesc: ProductDescriptionProperty[] = [];

		for (const property of variant.properties) {
			switch (property._type) {
				case 'sizeProperty':
					variantDesc.push({
						title: 'Størrelse',
						value: property.resolvedSize?.name ?? 'Uten navn',
					});
					break;
				case 'baseColorProperty':
					variantDesc.push({
						title:
							'Farge ' + '(' + (property.resolvedColor?.hex ?? 'Uten hekskode') + ')',
						value: property.resolvedColor?.name ?? 'Uten navn',
					});
					break;
				case 'printColorProperty':
					variantDesc.push({
						title:
							'Trykkfarge ' +
							'(' +
							(property.resolvedColor?.hex ?? 'Uten hekskode') +
							')',
						value: property.resolvedColor?.name ?? 'Uten navn',
					});
					break;
				case 'contrastColorProperty':
					variantDesc.push({
						title:
							'Kontrastfarge ' +
							'(' +
							(property.resolvedColor?.hex ?? 'Uten hekskode') +
							')',
						value: property.resolvedColor?.name ?? 'Uten navn',
					});
					break;
			}
		}

		desc.push({
			title: 'Variant',
			value: variantDesc,
		});
	}

	if (product._type === 'commerce.customClothingProduct' && configuration.options) {
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options?.[optionGroup._key];

			if (!configuredOption || !configuredOption.print) {
				continue;
			}

			let selectedColorName: string | null = null;
			let selectedColorHex: string | null = null;
			let selectedOptionName: string | null = null;
			let selectedCollectionName: string | null = null;

			for (const printCollection of optionGroup.printCollections ?? []) {
				if (printCollection._id === configuredOption.print?.collectionId) {
					for (const print of printCollection.prints ?? []) {
						if (print._key === configuredOption.print?.printKey) {
							if (
								configuredOption.printColorId &&
								printCollection._type === 'commerce.singleColorPrintCollection'
							) {
								const color = printCollection.availablePrintColors?.find(
									(color) => color._id === configuredOption.printColorId,
								);

								if (color) {
									selectedColorName = color?.name ?? null;
									selectedColorHex = color?.hex ?? null;
								} else if (
									print._type === 'singleColorPrint' &&
									configuredOption.printColorId === print.preferredPrintColor?._id
								) {
									selectedColorName = print.preferredPrintColor?.name ?? null;
									selectedColorHex = print.preferredPrintColor?.hex ?? null;
								}
							}
							selectedOptionName = print.name ?? 'Trykk uten navn';
							selectedCollectionName = printCollection.name ?? 'Samling uten navn';
							break;
						}
					}
				}
			}

			if (optionGroup.name) {
				if (selectedColorName) {
					desc.push({
						title: optionGroup.name ?? 'Trykkvalg uten navn',
						value: [
							{
								title: 'Trykksamling',
								value: selectedCollectionName ?? 'Samling uten navn',
							},
							{
								title: 'Trykknavn',
								value: selectedOptionName ?? 'Trykk uten navn',
							},
							{
								title:
									'Fargenavn ' +
									'(' +
									(selectedColorHex ?? 'Farge uten hekskode') +
									')',
								value: selectedColorName ?? 'Farge uten navn',
							},
						],
					});
				} else {
					desc.push({
						title: optionGroup.name ?? 'Trykkvalg uten navn',
						value: [
							{
								title: 'Trykksamling',
								value: selectedCollectionName ?? 'Samling uten navn',
							},
							{
								title: 'Trykknavn',
								value: selectedOptionName ?? 'Trykk uten navn',
							},
						],
					});
				}
			}
		}
	}

	return desc;
}

export function getImageStackForClothingConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	config: ClothingConfiguration | null,
	allowPlaceholders: boolean,
): ProductImageStackDescription | null {
	let selectedVariant = null;
	if (config && product.variants) {
		selectedVariant = product.variants.find((v) => v._key === config.variantId);
	}

	let image = imageFromSanityImage(product.images?.[side]);
	if (!image) {
		// We only consider this side if there is a base image for it:
		return null;
	}

	if (selectedVariant) {
		const img = imageFromSanityImage(selectedVariant.images?.[side]);
		if (img) {
			image = img;
		}
	}

	let overlays: Overlay[] = [];

	if (product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT && config) {
		overlays = (product.optionGroups ?? []).reduce((acc, optionGroup) => {
			let overlay: Overlay | null = null;
			const logoPlacement = optionGroup.overlayPlacement?.[side];
			if (!logoPlacement || !logoPlacement.isVisible) {
				return acc;
			}

			// Use the placeholder by default if the option group has one, and placeholders
			// are allowed in this context.
			if (allowPlaceholders && optionGroup.placeholderImage) {
				const img = imageFromSanityImage(optionGroup.placeholderImage);
				if (img) {
					overlay = {
						_type: 'imageOverlay',
						placement: getOverlayPlacement(logoPlacement),
						image: img,
					};
				}
			}

			const configuredOption = config.options?.[optionGroup._key];
			if (configuredOption) {
				for (const printCollection of optionGroup.printCollections ?? []) {
					// Now try to create an ovelay for the actual configured print, which will
					// override the placeholder if one is set:
					if (printCollection._id === configuredOption.print?.collectionId) {
						const configuredPrint = printCollection.prints?.find(
							(p) => p._key === configuredOption.print?.printKey,
						);
						if (!configuredPrint) {
							continue;
						}

						const placement = getOverlayPlacement(logoPlacement);
						switch (printCollection._type) {
							case 'commerce.multiColorPrintCollection': {
								const img = imageFromSanityImage(configuredPrint.image);
								if (img) {
									overlay = {
										_type: 'imageOverlay',
										placement: placement,
										image: img,
									};
								}
								break;
							}
							case 'commerce.singleColorPrintCollection': {
								const img = imageFromSanityImage(configuredPrint.image);
								if (img) {
									// Try to resolve the most appropriate color for the mask.
									let preferredColor: ProductColorFragment | null = null;

									const possibleColors: ProductColorFragment[] = [];
									for (const print of printCollection.prints ?? []) {
										if (print.preferredPrintColor) {
											if (print._key === configuredPrint._key) {
												preferredColor = print.preferredPrintColor;
											}
											possibleColors.push(print.preferredPrintColor);
										}
									}
									if (printCollection.availablePrintColors) {
										possibleColors.push(
											...printCollection.availablePrintColors,
										);
									}

									const firstColor =
										possibleColors.length > 0 ? possibleColors[0] : null;

									const configuredPrintColor = possibleColors.find(
										(c) => c._id === configuredOption.printColorId,
									);

									const maskColorId =
										configuredPrintColor?.hex ??
										preferredColor?.hex ??
										firstColor?.hex ??
										'#FFFFFF';

									// Add the mask overlay to the stack:
									overlay = {
										_type: 'maskedColorOverlay',
										placement: placement,
										maskImage: img,
										colorHex: maskColorId,
									};
								}
								break;
							}
							default:
								assertUnreachable(printCollection);
						}
					}
				}
			}

			if (overlay) {
				acc.push(overlay);
			}

			return acc;
		}, Array<Overlay>());
	}

	return {
		image,
		altText: '',
		overlays,
	};
}

export function getAllBaseClothingProductColors(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
) {
	const uniqueColorsById = new Map<string, ProductColorFragment>();

	for (const variant of product.variants ?? []) {
		for (const property of variant.properties ?? []) {
			switch (property._type) {
				case 'baseColorProperty': {
					const color = property.resolvedColor;
					if (color) {
						uniqueColorsById.set(color._id, color);
					}
					break;
				}
				case 'contrastColorProperty':
				case 'printColorProperty':
				case 'sizeProperty':
					// We only consider base colors here
					break;
				default:
					assertUnreachable(property);
			}
		}
	}

	return Array.from(uniqueColorsById.values());
}

function getAllClothingProductBrands(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
) {
	const uniqueBrandsById = new Map<string, BrandTagFragment>();

	if (product.brands) {
		for (const brand of product.brands) {
			uniqueBrandsById.set(brand._id, brand);
		}
	}

	if (product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT) {
		for (const optionGroup of product.optionGroups ?? []) {
			for (const printCollection of optionGroup.printCollections ?? []) {
				for (const print of printCollection.prints ?? []) {
					for (const brand of print.brands ?? []) {
						uniqueBrandsById.set(brand._id, brand);
					}
				}
			}
		}
	}

	return Array.from(uniqueBrandsById.values());
}

export const getConfiguredClothingProductBrands = (
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	configuration: ClothingConfiguration,
) => {
	const brandsById = new Map<string, BrandTagFragment>();

	// Add the base product brands:
	for (const brand of product.brands ?? []) {
		brandsById.set(brand._id, brand);
	}

	// Add all brands for the configured options:
	if (product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT && configuration.options) {
		for (const optionGroup of product.optionGroups ?? []) {
			const configuredOption = configuration.options?.[optionGroup._key];
			if (!configuredOption || !configuredOption.print) {
				continue;
			}

			for (const printCollection of optionGroup.printCollections ?? []) {
				if (printCollection._id === configuredOption.print.collectionId) {
					for (const print of printCollection.prints ?? []) {
						if (print._key === configuredOption.print.printKey) {
							for (const brand of print.brands ?? []) {
								brandsById.set(brand._id, brand);
							}
						}
					}
				}
			}
		}
	}

	return Array.from(brandsById.values());
};

export function getFiltersMatchingClothingProduct(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	filters: ProductFilters,
): ProductFilters | null {
	const matches = {
		colorIds: getAllBaseClothingProductColors(product).map((c) => c._id),
		brandIds: getAllClothingProductBrands(product).map((b) => b._id),
	};

	if (filters.colorIds && filters.colorIds.length > 0) {
		matches.colorIds = matches.colorIds.filter((colorId) =>
			(filters.colorIds ?? []).includes(colorId),
		);

		// If there are no matching colors left, the product doesn't match the selected color filters:
		if (matches.colorIds.length === 0) {
			return null;
		}
	}

	if (filters.brandIds && filters.brandIds.length > 0) {
		matches.brandIds = matches.brandIds.filter((brandId) =>
			(filters.brandIds ?? []).includes(brandId),
		);

		// If there are no matching brands left, the product doesn't match the selected brand filters:
		if (matches.brandIds.length === 0) {
			return null;
		}
	}

	return matches;
}

function getBrandedOptionGroupConfiguration(
	optionGroup: ClothingPrintOptionGroupCoreFragment,
	brandIds: string[],
): ClothingPrintOptionConfiguration | null {
	let optionConfig: ClothingPrintOptionConfiguration | null = null;

	for (const collection of optionGroup.printCollections ?? []) {
		let printColorId: string | null = null;

		if (
			collection._type === 'commerce.singleColorPrintCollection' &&
			collection.availablePrintColors &&
			collection.availablePrintColors.length
		) {
			printColorId = collection.availablePrintColors[0]._id;
		}

		const print = collection.prints?.find((print) =>
			print.brands?.find((brand) => brandIds.includes(brand._id)),
		);

		if (print) {
			if (print._type == 'singleColorPrint' && print.preferredPrintColor) {
				printColorId = print.preferredPrintColor._id;
			}

			optionConfig = {
				print: {
					collectionId: collection._id,
					printKey: print._key,
				},
				printColorId: printColorId,
			};

			break;
		}
	}

	return optionConfig;
}

export function getClothingConfigurationAppropriateForFilters(
	product: ClothingProductCoreFragment | CustomClothingProductCoreFragment,
	filters?: ProductFilters,
): ClothingConfiguration {
	const configuration = getDefaultClothingConfiguration(product);

	if (!filters) {
		return configuration;
	}

	// Find the first variant that matches one of the color filters:
	for (const colorId of filters.colorIds ?? []) {
		const variant = findVariantForSelectedProperties(
			product.variants ?? [],
			null,
			colorId,
			null,
			null,
		);

		if (variant) {
			configuration.variantId = variant._key;
			break;
		}
	}

	if (product._type === DOCUMENT_TYPE_CUSTOM_CLOTHING_PRODUCT) {
		// Search through the options groups, and enable prints branded according to the provided
		// brand filters. A print will always be configured for required option groups, but we
		// prefer to use one branded with one of the provided brands if possible.
		//
		// If we still haven't been able to find a branded option after the search through the
		// required option groups, we will search the optional option groups until we find at
		// least one.
		const requiredOptionGroups = product.optionGroups?.filter((group) => group.required) ?? [];
		let foundBrandedOption = false;
		for (const group of requiredOptionGroups) {
			// For required option groups, we always want to provide a configured print, but we prefer
			// to use one branded with one of the provided brands if possible:
			const brandedOptionConfig = getBrandedOptionGroupConfiguration(
				group,
				filters.brandIds ?? [],
			);
			if (brandedOptionConfig) {
				foundBrandedOption = true;
			}

			const optionConfig = brandedOptionConfig ?? getInitialOptionGroupConfiguration(group);

			if (optionConfig) {
				if (!configuration.options) {
					configuration.options = {};
				}

				configuration.options[group._key] = optionConfig;
			}
		}

		// If we still haven't been able to find a branded option, search the optional option groups as well:
		if (!foundBrandedOption) {
			const optionalOptionGroups =
				product.optionGroups?.filter((group) => !group.required) ?? [];
			for (const group of optionalOptionGroups) {
				const brandedOptionConfig = getBrandedOptionGroupConfiguration(
					group,
					filters.brandIds ?? [],
				);
				if (brandedOptionConfig) {
					if (!configuration.options) {
						configuration.options = {};
					}
					configuration.options[group._key] = brandedOptionConfig;
					break;
				}
			}
		}
	}

	return configuration;
}

export const clothingProductAdapter: ProductAdapter<
	ClothingProductFragment,
	ClothingProductCoreFragment
> = {
	getName: getProductNameForClothingConfiguration,
	getItems: getItemsForClothingConfiguration,
	getImages: getImageStackForClothingConfiguration,
	getDescription: getDescriptionForClothingConfiguration,
	isValidConfig: isValidClothingConfiguration,
	getMatchingFilters: getFiltersMatchingClothingProduct,
	getDefaultConfig: getDefaultClothingConfiguration,
	parseConfigFromFormData: clothingConfigurationFromFormData,
	parseConfigFromJSON: parseClothingConfiguration,
};

export const customClothingProductAdapter: ProductAdapter<
	CustomClothingProductFragment,
	CustomClothingProductCoreFragment
> = {
	getName: getProductNameForClothingConfiguration,
	getItems: getItemsForClothingConfiguration,
	getImages: getImageStackForClothingConfiguration,
	getDescription: getDescriptionForClothingConfiguration,
	isValidConfig: isValidClothingConfiguration,
	getMatchingFilters: getFiltersMatchingClothingProduct,
	getDefaultConfig: getDefaultClothingConfiguration,
	parseConfigFromFormData: clothingConfigurationFromFormData,
	parseConfigFromJSON: parseClothingConfiguration,
};
